"use client";

import { useEffect, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
export default function Casestudyshowcase() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const transportSolutions = [{
    id: "limpeza-cisternas",
    eyebrow: "01 — LIMPEZA",
    title: "Lavagem de Camiões-Cisterna e Vagões-Cisterna",
    description: "Garanta um transporte seguro e eficiente com soluções avançadas de pulverização para camiões-cisterna e vagões-cisterna. Os nossos sistemas automatizados reduzem o consumo de água, diminuem o tempo de limpeza e eliminam resíduos, ajudando-o a manter a conformidade, a prolongar a vida útil da frota e a manter as operações em movimento.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/limpeza_cisternas_transportes.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/limpeza_cisternas_transportes.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/limpeza_cisternas_transportes.png-wvc-sizes",
    alt: "Sistema de bicos especializados para limpeza de cisternas de transporte"
  }, {
    id: "lavagem-vagoes",
    eyebrow: "02 — FERROVIÁRIO",
    title: "Marítimo",
    description: "Otimize o tempo de rotação das embarcações com soluções de limpeza de elevado desempenho para petroleiros, navios e tanques marítimos. Os nossos sistemas automatizados removem resíduos difíceis, reduzem o consumo de água e minimizam os tempos de paragem, mantendo a sua frota eficiente, em conformidade e pronta para a próxima viagem.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lavagem_vagoes_ferroviarios.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lavagem_vagoes_ferroviarios.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lavagem_vagoes_ferroviarios.png-wvc-sizes",
    alt: "Sistema automatizado de lavagem para vagões ferroviários de transporte"
  }];
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduce: "(prefers-reduced-motion: reduce)",
        motion: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduce: boolean;
          motion: boolean;
        };
        if (conditions.reduce) {
          gsap.set("[data-reveal]", {
            opacity: 1,
            y: 0,
            clearProps: "all"
          });
          return;
        }
        gsap.from("[data-reveal='intro']", {
          opacity: 0,
          y: 24,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 82%"
          }
        });
        gsap.from("[data-reveal='card']", {
          opacity: 0,
          y: 36,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.15,
          scrollTrigger: {
            trigger: "[data-grid='cards']",
            start: "top 85%"
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="31406" ref={sectionRef} id="casestudyshowcase" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      <div className="mx-auto w-full max-w-[1360px]" style={{
      paddingInline: "clamp(20px, 4vw, 56px)"
    }}>
        {/* Header Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12 sm:mb-16">
          <div className="lg:col-span-7">
            <span data-reveal="intro" className="text-primary uppercase font-semibold text-xs sm:text-sm tracking-widest block mb-3">
              Setor de Transportes
            </span>
            <h2 data-reveal="intro" className="font-default font-semibold text-foreground tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">Transportes</h2>
          </div>

          <div className="lg:col-span-5 lg:text-right">
            <p data-reveal="intro" className="text-muted-foreground text-base sm:text-lg leading-relaxed">A Spraying Systems Co. disponibiliza soluções de pulverização críticas para aplicações de transporte — em terra e no mar. Os nossos bicos de limpeza de tanques e sistemas automatizados são de confiança para uma limpeza rápida e completa de camiões-cisterna, vagões-cisterna e embarcações marítimas, minimizando o consumo de água e de produtos químicos e maximizando a produtividade. Para a segurança contra incêndios, os nossos robustos bicos de pulverização industriais são projetados para atuar em ambientes de elevado calor e elevado risco — proporcionando uma supressão de incêndios fiável em navios, cais de carga e estações de abastecimento. </p>
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <div data-grid="cards" className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {transportSolutions.map((item, i) => <div key={item.id} data-index={i} data-reveal="card" className="group bg-card border border-border/80 rounded-3xl p-6 sm:p-8 flex flex-col justify-between transition-all duration-300 hover:border-primary/40 hover:shadow-lg">
              <div>
                {/* Image Container */}
                <div className="overflow-hidden rounded-2xl aspect-[3/2] w-full mb-6 bg-muted">
                  <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>

                {/* Eyebrow */}
                <span className="text-xs sm:text-sm font-mono tracking-wider text-muted-foreground uppercase block mb-2 font-medium">
                  {item.eyebrow}
                </span>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 tracking-tight">
                  {item.title}
                </h3>

                {/* Body Text */}
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>)}
        </div>

        {/* Footer CTA Strip */}
        <div className="mt-12 sm:mt-16 pt-8 border-t border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl leading-relaxed">
            Consulte a nossa equipa de engenharia para adaptar estas soluções às necessidades específicas da sua operação de transportes.
          </p>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-13 rounded-lg gap-3 pl-7 pr-2 shrink-0" style={{
          height: "52px"
        }}>
            <span className="text-sm font-medium">Consulte-nos</span>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background text-primary transition-transform duration-300 group-hover:-rotate-45" aria-hidden="true">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Button>
        </div>
      </div>
    </section>;
}